import { autoinject } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { NavigationInstruction, Next, PipelineStep } from 'aurelia-router';
import { CustomKeysDialog } from '~/components/dialog/custom-keys-dialog';
import { SimpleDialogType } from '~/components/dialog/simple-dialog';
import { DialogUtils } from '~/utils/dialog-utils';
import { DirtyCheckAggregator } from '~/utils/dirty-check-aggregator';
import { isInTreeView } from '~/utils/utils';
import { Logger } from '~/utils/logger';

@autoinject
export class DirtyCheckStep implements PipelineStep {
	private constructor(
		private readonly dirtyCheckAggregator: DirtyCheckAggregator,
		private readonly dialogUtils: DialogUtils,
		public i18n: I18N,
	) {}

	public run(instruction: NavigationInstruction, next: Next): Promise<any> {
		const anyFormHasChanges = this.dirtyCheckAggregator.performDirtyCheck();
		if (!anyFormHasChanges) {
			this.dirtyCheckAggregator.reset(); // clear old listeners are cleared before loading new page
			return next();
		}
		/**
		 * In treeView, a java swing dialog will handle the prompt
		 */
		if (isInTreeView()) {
			this.dirtyCheckAggregator.reset();
			return next();
		}

		return this.promptUserForConfirmation()
			.then(() => {
				this.dirtyCheckAggregator.reset(); // clear old listeners are cleared before loading new page
				return next();
			})
			.catch(next.cancel);
	}

	/**
	 * Get user confirmation before discarding changes
	 */
	private promptUserForConfirmation(): Promise<void> {
		const dialogModel = this.dialogUtils.getSimpleDialogModel({
			bodyText: this.i18n.tr('slideout.unsaved-changes-will-be-lost-ask'),
			simpleDialogType: SimpleDialogType.WARNING,
		});
		return new Promise((resolve, reject) => {
			this.dialogUtils
				.showCustomDialog(CustomKeysDialog, dialogModel)
				.whenClosed((value) => {
					value.wasCancelled ? reject() : resolve();
				});
		});
	}
}
